import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiClientError } from "../api";
import type {
  EffectivePermission,
  Id,
  PermissionScope,
  StaffUser,
} from "../types";
import { useToast } from "../context/ToastContext";

const SCOPE_LABELS: Record<PermissionScope, string> = {
  own: "Propio",
  assigned: "Asignados",
  team: "Equipo",
  all: "Todo",
};

const SOURCE_LABELS: Record<EffectivePermission["source"], string> = {
  ROLE: "Heredado del rol",
  USER_ALLOW: "Permitido individualmente",
  USER_DENY: "Denegado individualmente",
  MEMBERSHIP_ALLOW: "Permitido en esta organización",
  MEMBERSHIP_DENY: "Denegado en esta organización",
  NONE: "Sin acceso",
};

const MODULE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  executive: "Ejecutivo",
  contacts: "Pacientes y contactos",
  commercial: "Comercial",
  tasks: "Tareas",
  sales: "Ventas",
  agenda: "Agenda",
  clinical: "Clínica",
  postcare: "Postventa",
  issues: "Incidencias",
  retention: "Retención",
  economics: "Economía",
  marketing: "Marketing",
  reports: "Reportes",
  audit: "Auditoría",
  configuration: "Configuración",
  security: "Seguridad",
};

export default function UserPermissionsEditor({
  user,
  onClose,
}: {
  user: StaffUser;
  onClose: () => void;
}) {
  const { show } = useToast();

  const [permissions, setPermissions] = useState<EffectivePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyCode, setBusyCode] = useState<string | null>(null);

  const [scopeChoices, setScopeChoices] = useState<Record<string, PermissionScope>>({});
  const scrollRef = useRef<HTMLDivElement | null>(null);

  async function load(preserveScroll = false) {
    const previousScrollTop =
      preserveScroll && scrollRef.current
        ? scrollRef.current.scrollTop
        : null;

    // Solo mostramos el estado de carga completo en la carga inicial.
    // Los refresh posteriores son silenciosos para evitar que la lista
    // se desmonte y produzca un parpadeo visual.
    const initialLoad = permissions.length === 0;

    if (initialLoad) {
      setLoading(true);
    }

    try {
      const result = await api.getUserPermissions(user.id);
      setPermissions(result.permissions);

      const nextScopes: Record<string, PermissionScope> = {};

      for (const permission of result.permissions) {
        nextScopes[permission.code] =
          permission.scope ||
          permission.allowed_scopes[0] ||
          "all";
      }

      setScopeChoices(nextScopes);

      if (previousScrollTop !== null) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTop = previousScrollTop;
            }
          });
        });
      }
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudieron cargar los permisos.", "error");
    } finally {
      if (initialLoad) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  function updateEffectivePermission(
    effective: EffectivePermission
  ) {
    setPermissions((prev) =>
      prev.map((item) =>
        item.code === effective.code ? effective : item
      )
    );

    setScopeChoices((prev) => ({
      ...prev,
      [effective.code]:
        effective.scope ||
        effective.allowed_scopes[0] ||
        prev[effective.code] ||
        "all",
    }));
  }

  const grouped = useMemo(() => {
    const result = new Map<string, EffectivePermission[]>();

    for (const permission of permissions) {
      if (!result.has(permission.module)) {
        result.set(permission.module, []);
      }

      result.get(permission.module)!.push(permission);
    }

    return [...result.entries()];
  }, [permissions]);

  async function allow(permission: EffectivePermission) {
    const scope =
      scopeChoices[permission.code] ||
      permission.allowed_scopes[0];

    if (!scope) {
      show("Este permiso no tiene alcance válido configurado.", "error");
      return;
    }

    setBusyCode(permission.code);

    try {
      const result = await api.setUserPermission(
        user.id,
        permission.code,
        {
          allowed: true,
          scope,
        }
      );

      updateEffectivePermission(result.effective);

      show("Permiso individual concedido.", "success");

    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo conceder el permiso.", "error");
    } finally {
      setBusyCode(null);
    }
  }

  async function deny(permission: EffectivePermission) {
    setBusyCode(permission.code);

    try {
      const result = await api.setUserPermission(
        user.id,
        permission.code,
        {
          allowed: false,
        }
      );

      updateEffectivePermission(result.effective);

      show("Permiso individual denegado.", "success");

    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo denegar el permiso.", "error");
    } finally {
      setBusyCode(null);
    }
  }

  async function useRole(permission: EffectivePermission) {
    setBusyCode(permission.code);

    try {
      const result = await api.removeUserPermissionOverride(
        user.id,
        permission.code
      );

      updateEffectivePermission(result.effective);

      show("El permiso vuelve a usar la configuración del rol.", "success");

    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo restaurar el permiso del rol.", "error");
    } finally {
      setBusyCode(null);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        ref={scrollRef}
        className="modal modal-wide permissions-editor-modal"
        style={{ maxWidth: 1000, maxHeight: "88vh", overflow: "auto" }}
        onClick={(e: any) => e.stopPropagation()}
      >
        <div
          className="kanban-header"
          style={{ alignItems: "flex-start" }}
        >
          <div>
            <h3>Permisos individuales</h3>

            <p className="meta">
              <b>{user.full_name}</b>
              <br />
              {user.email}
              <br />
              Rol base: {user.role_name || user.role_code}
            </p>
          </div>

          <button className="btn-ghost" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <p className="meta">
          Los permisos del rol se usan por defecto. Una excepción individual puede
          permitir o denegar un acceso específico sin cambiar el rol del usuario.
        </p>

        {loading && (
          <div className="loading-strip">
            Cargando permisos...
          </div>
        )}

        {!loading &&
          grouped.map(([module, modulePermissions]) => (
            <div key={module} style={{ marginTop: 24 }}>
              <h3>
                {MODULE_LABELS[module] || module}
              </h3>

              <div className="contact-list">
                {modulePermissions.map((permission) => {
                  const busy = busyCode === permission.code;

                  return (
                    <div
                      key={permission.code}
                      className="contact-row"
                      style={{ alignItems: "flex-start" }}
                    >
                      <div style={{ flex: 1, minWidth: 240 }}>
                        <div className="name">
                          {permission.name}
                        </div>

                        <div className="meta">
                          {permission.description}
                        </div>

                        <div className="meta" style={{ marginTop: 6 }}>
                          Código: {permission.code}
                        </div>

                        <div style={{ marginTop: 8 }}>
                          <span
                            className={`pill ${
                              permission.allowed
                                ? "pill-ok"
                                : "pill-bad"
                            }`}
                          >
                            {permission.allowed
                              ? "Permitido"
                              : "Sin acceso"}
                          </span>

                          <span
                            className="pill"
                            style={{ marginLeft: 6 }}
                          >
                            {SOURCE_LABELS[permission.source]}
                          </span>

                          {permission.scope && (
                            <span
                              className="pill"
                              style={{ marginLeft: 6 }}
                            >
                              Alcance: {SCOPE_LABELS[permission.scope]}
                            </span>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <select
                          value={
                            scopeChoices[permission.code] ||
                            permission.allowed_scopes[0]
                          }
                          disabled={
                            busy ||
                            permission.allowed_scopes.length <= 1
                          }
                          onChange={(e: any) =>
                            setScopeChoices((prev) => ({
                              ...prev,
                              [permission.code]:
                                e.target.value as PermissionScope,
                            }))
                          }
                        >
                          {permission.allowed_scopes.map((scope) => (
                            <option key={scope} value={scope}>
                              {SCOPE_LABELS[scope]}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          className="btn-primary"
                          aria-busy={busy}
                          onClick={() => {
                            if (!busy) allow(permission);
                          }}
                        >
                          Permitir
                        </button>

                        <button
                          type="button"
                          className="btn-ghost"
                          aria-busy={busy}
                          onClick={() => {
                            if (!busy) deny(permission);
                          }}
                        >
                          Denegar
                        </button>

                        <button
                          type="button"
                          className="btn-ghost"
                          disabled={
                            !["USER_ALLOW", "USER_DENY"].includes(
                              permission.source
                            )
                          }
                          aria-busy={busy}
                          onClick={() => {
                            if (!busy) useRole(permission);
                          }}
                        >
                          Usar rol
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

        {!loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              marginTop: 28,
              paddingTop: 16,
              borderTop: "1px solid var(--border, #ddd)",
            }}
          >
            <button
              type="button"
              className="btn-ghost"
              onClick={() =>
                scrollRef.current?.scrollTo({
                  top: 0,
                  behavior: "smooth",
                })
              }
            >
              ↑ Volver arriba
            </button>

            <button
              type="button"
              className="btn-primary"
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
